import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DateField from '../components/DateField.jsx';
import { api, withQuery, money, date, PAYMENT_LABEL } from '../api.js';
import { Pager } from './Products.jsx';

const PAGE = 50;

export default function Payments() {
  const [filters, setFilters] = useState({ from: '', to: '', direction: '', partnerQ: '' });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, inEur: '0.00', outEur: '0.00' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    // При писане на име — изчаква да спре, за да не тегли на всяка буква.
    const timer = setTimeout(() => {
      setLoading(true);
      api.get(withQuery('/api/payments', { ...filters, limit: PAGE, offset }))
        .then(setData)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, filters.partnerQ ? 250 : 0);
    return () => clearTimeout(timer);
  }, [filters, offset, reload]);

  function change(patch) {
    setOffset(0);
    setFilters((f) => ({ ...f, ...patch }));
  }

  return (
    <>
      <div className="page-head">
        <h1>Плащания</h1>
        <div className="spacer" />
        <span className="badge ok">приход {money(data.inEur)}</span>
        <span className="badge danger">разход {money(data.outEur)}</span>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <label className="field">
          Контрагент
          <input
            type="search"
            autoComplete="off"
            placeholder="име, ЕИК или ДДС номер"
            value={filters.partnerQ}
            onChange={(e) => change({ partnerQ: e.target.value })}
          />
        </label>
        <label className="field">
          От дата
          <DateField value={filters.from} onChange={(v) => change({ from: v })} />
        </label>
        <label className="field">
          До дата
          <DateField value={filters.to} onChange={(v) => change({ to: v })} />
        </label>
        <label className="field">
          Посока
          <select value={filters.direction} onChange={(e) => change({ direction: e.target.value })}>
            <option value="">всички</option>
            <option value="in">приход</option>
            <option value="out">разход</option>
          </select>
        </label>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Номер</th>
                <th>Дата</th>
                <th>Контрагент</th>
                <th>Каса / банка</th>
                <th>Начин</th>
                <th className="num">Сума</th>
                <th>Съставил</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} className="clickable" onClick={() => setOpenId(p.id)}>
                  <td>
                    {p.payment_number}
                    {!!Number(p.is_storno) && <span className="badge danger" style={{ marginLeft: 6 }}>сторно</span>}
                    {!!Number(p.is_stornoed) && <span className="badge" style={{ marginLeft: 6 }}>върнато</span>}
                  </td>
                  <td>{date(p.payment_date)}</td>
                  <td>{p.partner_name ?? <span className="muted">—</span>}</td>
                  <td className="muted">{p.cash_register_name ?? '—'}</td>
                  <td className="muted">{PAYMENT_LABEL[p.method]}</td>
                  <td className="num" style={{ color: p.direction === 'in' ? 'var(--ok)' : 'var(--danger)' }}>
                    {p.direction === 'in' ? '+' : '−'}{money(p.amount, p.currency)}
                  </td>
                  <td className="muted">{p.created_by_name ?? '—'}</td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={7} className="empty">Няма плащания</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager total={data.total} offset={offset} setOffset={setOffset} loading={loading} page={PAGE} />
      </div>

      {openId && (
        <PaymentModal
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => setReload((n) => n + 1)}
        />
      )}
    </>
  );
}

function Red({ k, v }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '3px 0' }}>
      <span className="muted" style={{ minWidth: 130 }}>{k}</span><span>{v}</span>
    </div>
  );
}

/**
 * Едно плащане (19.10.2026): подробности, по кои документи е разнесено и
 * „Върни плащането“. Връщането пита първо; след потвърждение сървърът
 * записва насрещно (сторно) плащане — продажбата става неплатена, сумата
 * излиза от касата/банката, а при плащане в брой отива служебно изведена
 * сума към апарата.
 */
function PaymentModal({ id, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  function zaredi() {
    api.get(`/api/payments/${id}`).then(setP).catch((e) => setError(e.message));
  }
  useEffect(() => { zaredi(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const vBroy = p && ['cash', 'cash_deferred'].includes(p.method);
  const kade = vBroy ? 'касата' : 'банковата сметка';

  async function varni() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/api/payments/${id}/storno`, { reason: reason.trim() || null });
      setDone(`Плащането е върнато — сторно № ${r.paymentNumber}.`
        + (r.documents.length ? ` Отново неплатени: ${r.documents.join(', ')}.` : '')
        + (r.fiscal === 'service_out' ? ' Към апарата е пуснат бон за служебно изведена сума.' : ''));
      setAsking(false);
      zaredi();
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 640 }}>
        <div className="card-head">
          Плащане {p ? `№ ${p.payment_number}` : ''}
          {p && !!Number(p.is_storno) && <span className="badge danger" style={{ marginLeft: 8 }}>сторно</span>}
          {p?.stornoed_by_number && <span className="badge" style={{ marginLeft: 8 }}>върнато</span>}
        </div>
        <div className="card-body">
          {!p && !error && <div className="empty">Зареждане…</div>}
          {error && <div className="error">{error}</div>}
          {done && <div className="notice">{done}</div>}
          {p && (
            <>
              <Red k="Дата" v={date(p.payment_date)} />
              <Red k="Контрагент" v={p.partner_name ?? '—'} />
              <Red k="Сума" v={(
                <strong style={{ color: p.direction === 'in' ? 'var(--ok)' : 'var(--danger)' }}>
                  {p.direction === 'in' ? '+' : '−'}{money(p.amount, p.currency)}
                </strong>
              )} />
              <Red k="Начин" v={p.method_name} />
              <Red k="Каса / банка" v={p.cash_register_name ?? '—'} />
              {p.bank_ref && <Red k="Банково основание" v={p.bank_ref} />}
              {p.note && <Red k="Бележка" v={p.note} />}
              <Red k="Съставил" v={p.created_by_name ?? '—'} />
              {p.storno_of_number && <Red k="Сторно на" v={`плащане № ${p.storno_of_number}`} />}
              {p.stornoed_by_number && <Red k="Върнато със" v={`сторно № ${p.stornoed_by_number}`} />}

              <div style={{ margin: '12px 0 4px', fontWeight: 600 }}>Разнесено по</div>
              {p.allocations.length === 0 ? (
                <div className="muted">не е разнесено по документ — аванс по сметката</div>
              ) : (
                <table className="compact">
                  <thead>
                    <tr><th>Документ</th><th>Дата</th><th>Фактура</th><th className="num">Сума</th></tr>
                  </thead>
                  <tbody>
                    {p.allocations.map((a, i) => (
                      <tr key={i}>
                        <td><Link to={`/documents/${a.document_id}`} onClick={onClose}>{a.doc_number}</Link></td>
                        <td>{date(a.doc_date)}</td>
                        <td className="muted">{a.invoice_no ?? '—'}</td>
                        <td className="num">{money(a.amount, a.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {asking && (
                <div className="warning" style={{ marginTop: 14 }}>
                  <strong>Да анулирам ли плащане № {p.payment_number} за {money(p.amount, p.currency)}?</strong>
                  <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                    {p.allocations.length > 0 && (
                      <li>Отново неплатени: {p.allocations.map((a) => a.doc_number).join(', ')}.</li>
                    )}
                    <li>Сумата излиза от {kade}.</li>
                    {vBroy && p.direction === 'in' && <li>Към апарата ще се пусне бон за служебно изведена сума.</li>}
                    <li>Плащането не се изтрива — остава в историята със сторно срещу него.</li>
                  </ul>
                  <label className="field" style={{ marginTop: 6 }}>
                    Причина (по желание)
                    <input value={reason} autoComplete="off" onChange={(e) => setReason(e.target.value)}
                      placeholder="напр. платена грешна фактура" />
                  </label>
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-foot">
          {asking ? (
            <>
              <button onClick={() => setAsking(false)} disabled={busy}>Не</button>
              <button className="danger" onClick={varni} disabled={busy}>
                {busy ? 'Връщане…' : 'Да, анулирай плащането'}
              </button>
            </>
          ) : (
            <>
              {p?.method === 'card' && !Number(p.is_storno) && (
                <span className="muted" style={{ marginRight: 'auto' }}>Плащане с карта се връща със сторно на продажбата.</span>
              )}
              {p?.can_storno && !done && (
                <button className="danger" onClick={() => setAsking(true)}>Върни плащането</button>
              )}
              <button onClick={onClose}>Затвори</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
